import { Route, Routes } from '@angular/router';

import { importProvidersFrom } from '@angular/core';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { CourseContentEffects, courseContentFeature } from '@shared';
import { AuthenticationGuard } from '@shared/services/auth/authentication.guard';
import { InstructorGuard } from '@shared/services/auth/instructor.guard';

/**
 * Provides helper methods to create routes.
 */
export class Shell {
  /**
   * Creates routes using the shell component and authentication.
   * @param routes The routes to add.
   * @return The new route using shell as the base.
   */
  static childRoutesForTermsAndCondition(routes: Routes): Route {
    // return {
    //   path: '',
    //   component: TermsShellComponent,
    //   children: routes,
    //   canActivate: [],
    // };

    return {
      path: '',
      loadComponent: () => import('@app/shell/terms-shell/terms-shell.component').then((m) => m.TermsShellComponent),
      loadChildren: () => import('@app/terms/terms-routing.module').then((m) => m.routes),
    };
  }

  static childRoutesForWelcome(routes: Routes): Route {
    return {
      path: '',
      loadComponent: () =>
        import('@app/shell/components/welcome-shell/welcome-shell.component').then((m) => m.WelcomeShellComponent),
      loadChildren: () => import('@app/welcome/welcome-routing.module').then((m) => m.routes),
    };
  }

  static childRoutesForPublic(routes: Routes): Route {
    return {
      path: '',
      loadComponent: () =>
        import('@app/shell/general-shell/general-shell.component').then((m) => m.GeneralShellComponent),
      loadChildren: () => import('@app/public/public-routing.module').then((m) => m.routes),
    };
  }

  static childRoutesForStudent(routes: Routes): Route {
    return {
      path: 'student',
      canActivate: [AuthenticationGuard],
      loadComponent: () =>
        import('@app/shell/general-shell/general-shell.component').then((m) => m.GeneralShellComponent),
      loadChildren: () => import('@app/student/student-routing.module').then((m) => m.routes),
    };
  }

  static childRoutesForInstructor(routes: Routes): Route {
    return {
      path: 'instructor',
      canActivate: [AuthenticationGuard],
      loadComponent: () =>
        import('@app/shell/instructor-shell/instructor-shell.component').then((m) => m.InstructorShellComponent),
      loadChildren: () => import('@app/instructor/instructor-routing.module').then((m) => m.InstructorChildRoute),
    };
  }

  static childRoutesForInstructorAssignment(routes: Routes): Route {
    return {
      path: 'instructor',
      canActivate: [AuthenticationGuard],
      loadComponent: () =>
        import('@app/shell/instructor-shell/instructor-shell.component').then((m) => m.InstructorShellComponent),
      loadChildren: () =>
        import('@app/instructor/instructor-routing.module').then((m) => m.InstructorAssignmentsChildRoute),
    };
  }

  static childRoutesForInstructorCourse(routes: Routes): Route {
    return {
      path: 'instructor/course',
      canActivate: [AuthenticationGuard, InstructorGuard],
      loadComponent: () =>
        import('@app/shell/instructor-shell/instructor-shell.component').then((m) => m.InstructorShellComponent),
      loadChildren: () =>
        import('@app/instructor/instructor-routing.module').then((m) => m.InstructorCoursesChildRoute),
    };
  }

  static childRoutesForAccount(routes: Routes): Route {
    return {
      path: 'auth',
      loadComponent: () =>
        import('@app/shell/account-shell/account-shell.component').then((m) => m.AccountShellComponent),
      loadChildren: () => import('@app/account/account-routing.module').then((m) => m.routes),
    };
  }

  static childRoutesForLearningLab(routes: Routes): Route {
    return {
      path: 'learning-lab',
      providers: [
        importProvidersFrom(
          StoreModule.forFeature({ ...courseContentFeature }),
          EffectsModule.forFeature([CourseContentEffects])
        ),
      ],
      loadComponent: () =>
        import('@app/shell/learninglab-shell/learninglab-shell.component').then((m) => m.LearninglabShellComponent),
      loadChildren: () => import('@app/learning-lab/learning-lab-routing.module').then((m) => m.routes),
    };
  }

  static childRoutesForLearningLabCertificate(routes: Routes): Route {
    return {
      path: 'certificate',
      providers: [
        importProvidersFrom(
          StoreModule.forFeature({ ...courseContentFeature }),
          EffectsModule.forFeature([CourseContentEffects])
        ),
      ],
      loadComponent: () =>
        import('@app/shell/learninglab-certificate-shell/learninglab-certificate-shell.component').then(
          (m) => m.LearninglabCertificateShellComponent
        ),
      loadChildren: () =>
        import('@app/learning-lab-certificate/learning-lab-certificate-routing.module').then((m) => m.routes),
    };
  }
}
